import type {
  ClientFrame,
  ServerFrame,
  StoredMessage,
} from "../types";

export interface WSCallbacks {
  onReady?: (data: { reply_count: number; messages: StoredMessage[] }) => void;
  onToken?: (delta: string) => void;
  onMessage?: (content: string) => void;
  onToolCall?: (name: string, status: "running" | "done") => void;
  onError?: (code: string, message: string) => void;
  onEnded?: (reason: string) => void;
  onDisconnect?: () => void;
  // The agent closed this socket because the same session connected
  // elsewhere (another tab). Don't auto-reconnect, or the tabs would keep
  // taking the session from each other.
  onReplaced?: () => void;
}

// Close code the agent uses when a newer socket takes over the session.
const CLOSE_REPLACED = 4000;

// Thin WebSocket wrapper. Intentionally does NOT reconnect on drop —
// the agent's 90s grace window means a reload keeps the same session
// in Redis, and the next bootstrap (or stored resume) reopens cleanly.
// Auto-reconnect on a transient drop would race with finalize.
export class ChatWS {
  private ws: WebSocket | null = null;
  private url: string;
  private callbacks: WSCallbacks;
  private intentionallyClosed = false;

  constructor(url: string, callbacks: WSCallbacks) {
    this.url = url;
    this.callbacks = callbacks;
  }

  connect(): void {
    this.intentionallyClosed = false;
    this.ws = new WebSocket(this.url);

    this.ws.onmessage = (event) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data) as ServerFrame;
      } catch {
        return;
      }
      this.dispatch(frame);
    };

    this.ws.onclose = (event) => {
      if (this.intentionallyClosed) return;
      if (event.code === CLOSE_REPLACED && this.callbacks.onReplaced) {
        this.callbacks.onReplaced();
        return;
      }
      this.callbacks.onDisconnect?.();
    };

    this.ws.onerror = () => {
      // The close handler fires shortly after; we let it dispatch.
    };
  }

  // Returns true if the frame was actually written to the socket.
  // A false return is the caller's cue to queue and reconnect — the
  // earlier version silently dropped frames on a closed socket, which
  // made post-idle messages vanish without any user feedback.
  send(frame: ClientFrame): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return true;
    }
    return false;
  }

  close(): void {
    this.intentionallyClosed = true;
    try {
      this.ws?.close();
    } catch {
      // ignore
    }
    this.ws = null;
  }

  private dispatch(frame: ServerFrame): void {
    switch (frame.type) {
      case "ready":
        this.callbacks.onReady?.({
          reply_count: frame.reply_count,
          messages: frame.messages,
        });
        break;
      case "token":
        this.callbacks.onToken?.(frame.delta);
        break;
      case "message":
        this.callbacks.onMessage?.(frame.content);
        break;
      case "tool_call":
        this.callbacks.onToolCall?.(frame.name, frame.status);
        break;
      case "error":
        this.callbacks.onError?.(frame.code, frame.message);
        break;
      case "session_ended":
        this.callbacks.onEnded?.(frame.reason);
        break;
    }
  }
}
